import { action, computed, observable, makeObservable } from 'mobx';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../../commonv3/api/RemoteResourceV3';
import { MembershipTokenIndexView } from '../../../../types/membershipTokens';
import { MembershipTokenApis } from '../../../../apis/membershipTokens';
import { createSummaries, MembershipSummary } from '../../../../types/membershipSummaries';
import { DialogState } from '../../../../../commonv3/states/dialogState';
import { MembershipItemId } from '../../../../../commonv3/types/membershipPlans';
import { MembershipUpdateRequestApis, OperationType } from '../../../../apis/membershipUpdateRequests';
import { SubmitState } from '../../../../../commonv3/states/SubmitState';
import { translateErrorMessage } from '../../../../../commonv3/api/protocol';
import { SubscriptionsApis } from '../../../../apis/subscriptions';
import { SubscriptionSummary } from '../../../../types/subscriptionSummary';
import { SubscriptionSummariesApis } from '../../../../apis/subscriptionSummaries';
import { Service } from '../../../../../commonv3/types/service';
import { createContext, useContext, useEffect } from 'react';

export class PlanPaneState {
  tokens: RemoteResourceV3<MembershipTokenIndexView[]> = RemoteResourceV3.loading();
  cancelUpdateDialog = new DialogState();
  cancelRequest = null;
  submitState = new SubmitState();
  subscriptionSummaries: RemoteResourceV3<SubscriptionSummary[]> = RemoteResourceV3.loading();
  initialized = false;
  subscriptionResumeRequest = null;
  resumeSubscriptionDialog = new DialogState();
  resumeSubscriptionSubmit = new SubmitState();
  resumeSubscriptionCompletionDialog = new DialogState();

  constructor() {
    makeObservable(this, {
      tokens: observable,
      cancelUpdateDialog: observable,
      cancelRequest: observable,
      submitState: observable,
      subscriptionSummaries: observable,
      initialized: observable,
      subscriptionResumeRequest: observable,
      resumeSubscriptionDialog: observable,
      resumeSubscriptionSubmit: observable,
      resumeSubscriptionCompletionDialog: observable,
      init: action.bound,
      loading: computed,
      loadTokens: action.bound,
      membershipTokens: computed,
      membershipSummaries: computed,
      hasSubscription: computed,
      requestCancelUpdate: action.bound,
      cancelUpdate: action.bound,
      requestResumeSubscription: action.bound,
      resumeSubscription: action.bound,
      cancelSuspension: action.bound,
    });
  }

  async init() {
    await this.loadTokens();
    await this.reloadSubscriptionSummaries();
    this.initialized = true;
  }

  get loading() {
    return this.tokens.loading || this.subscriptionSummaries.loading;
  }

  async loadTokens() {
    await updateRemoteResourceV3(
      this.tokens,
      (v) => (this.tokens = v),
      () => MembershipTokenApis.findAll(Service.LIKES)
    );
  }

  async reloadSubscriptionSummaries() {
    await updateRemoteResourceV3(
      this.subscriptionSummaries,
      (v) => (this.subscriptionSummaries = v),
      () => SubscriptionSummariesApis.findAll([Service.LIKES])
    );
  }

  get membershipTokens(): MembershipTokenIndexView[] {
    return this.tokens.getOrElse([]);
  }

  get membershipSummaries(): MembershipSummary[] {
    return createSummaries(this.tokens.getOrElse([]));
  }

  get hasSubscription(): boolean {
    return this.membershipTokens.some((token) => token.subscribed);
  }

  requestCancelUpdate(currentItemId: MembershipItemId, nextItemId: MembershipItemId) {
    this.cancelRequest = {
      currentItemId,
      nextItemId,
    };
    this.cancelUpdateDialog.open();
  }

  async cancelUpdate() {
    await this.submitState.operate(async () => {
      try {
        await MembershipUpdateRequestApis.create(
          this.cancelRequest.nextItemId,
          this.cancelRequest.currentItemId,
          OperationType.CANCEL
        );
        await this.init();
        this.cancelUpdateDialog.close();
      } catch (e) {
        alert('キャンセルに失敗しました: ' + translateErrorMessage(e));
      }
    });
  }

  requestResumeSubscription(subscriptionCode: string) {
    this.subscriptionResumeRequest = { subscriptionCode };

    this.resumeSubscriptionDialog.open();
  }

  async resumeSubscription() {
    await this.resumeSubscriptionSubmit.operate(async () => {
      try {
        const { subscriptionCode } = this.subscriptionResumeRequest;

        await SubscriptionsApis.resume_subscription(subscriptionCode);
        await this.init();
        this.resumeSubscriptionDialog.close();
        this.resumeSubscriptionCompletionDialog.open();
      } catch (e) {
        alert('キャンセルに失敗しました: ' + translateErrorMessage(e));
      }
    });
  }

  async cancelSuspension(subscriptionCode: string, subscriptionSuspensionCode: string) {
    try {
      await this.submitState.operate(async () => {
        SubscriptionsApis.cancel_suspend(subscriptionCode, subscriptionSuspensionCode);
      });
      await this.init();
    } catch (e) {
      alert('休会予約のキャンセルに失敗しました: ' + translateErrorMessage(e));
    }
  }
}

const PlanPaneContext = createContext(new PlanPaneState());

/**
 * 退会申請フォームでもプランの情報を取得したいため、Contextを利用して同じステートを参照できるようにする。
 * 最初にこのコンポーネントを読み込んだ時点で初期化される。
 * @return PlanPaneState
 */
export const usePlanPaneState = (): PlanPaneState => {
  const state = useContext(PlanPaneContext);
  useEffect(() => {
    if (!state.initialized) {
      state.init();
    }
  }, []);
  return state;
};
